import { type Token, TokenKind } from '../../json/tokens';
import { isCloser, isOpener, scanStructure, structuralTokens } from '../support';
import type { RepairContext, RepairProposal, RepairRule } from '../types';

/**
 * Delete a `,` that separates nothing.
 *
 * Three shapes, all decided from neighbouring tokens:
 *   - leading, right after `{` or `[` — `{,"a":1}`, `[,1]`;
 *   - repeated, right after another comma — `[1,,2]`;
 *   - dangling at the top level with only trivia behind it — `{"a":1}  ,  `, the case the
 *     legacy pipeline could only fix as a side effect of an unrelated bracket repair
 *     (recon §3.5, B46).
 *
 * A comma directly before a closer belongs to `remove-trailing-comma`; skipping it here
 * keeps the two rules from proposing the same edit and filling `skipped` with noise.
 *
 * Negative case pinned in the test: `{"a":"1,,2"}` and `{"a":"[,x"}`, both of which the
 * legacy `/,\s*,+/g` and `/([{\[])\s*,/g` corrupted silently.
 */
export const removeStrayCommaRule: RepairRule = {
  id: 'remove-stray-comma',
  confidence: 'safe',
  priority: 21,
  resolves: ['syntax/unexpected-comma'],

  detect(ctx: RepairContext): readonly RepairProposal[] {
    const sig = structuralTokens(ctx.source, ctx.tokens);
    const dangling = new Set<Token>(scanStructure(ctx.source, ctx.tokens).danglingCommas);
    const proposals: RepairProposal[] = [];

    for (let i = 0; i < sig.length; i += 1) {
      const token = sig[i];
      if (token === undefined || token.kind !== TokenKind.Comma) continue;

      const previous = sig[i - 1];
      const next = sig[i + 1];
      if (next !== undefined && isCloser(next)) continue;

      const stray =
        dangling.has(token) ||
        (previous !== undefined && (isOpener(previous) || previous.kind === TokenKind.Comma));
      if (!stray) continue;

      proposals.push({
        ruleId: 'remove-stray-comma',
        span: { from: token.from, to: token.to },
        edits: [{ from: token.from, to: token.to, insert: '' }],
        labelCode: 'fix.remove-stray-comma',
        resolves: 'syntax/unexpected-comma',
      });
    }

    return proposals;
  },
};
